<template>
    <div class="map-wrapper" :class="{'compact-map': compact}">
        <div ref="map" id="ol-map-container" class="w-100" :style="{height: mapHeight}"></div>
        <div v-if="showVesselPanel" class="vessel-info-panel">
            <div v-if="selectedItem.senior" class="panel-info-line">
                <div class="panel-info-label">船名：</div>
                <a class="panel-info-content anchor" target="_blank" :title="selectedItem.displayName"
                   :href="'/voyage.html?hash=' + selectedItem.senior.hash + (delegation ? ('&delegation=' + delegation) : '') +'&origin=blank'">
                    {{selectedItem.displayName}}
                </a>
            </div>
            <div v-else class="panel-info-line">
                <div class="panel-info-label">船名：</div>
                <div class="panel-info-content" :title="selectedItem.displayName">{{selectedItem.displayName}}</div>
            </div>
            <div v-if="selectedItem.weight" class="panel-info-line">
                <div class="panel-info-label">载重吨：</div>
                <div class="panel-info-content">{{selectedItem.weight}}吨</div>
            </div>
            <div v-if="selectedItem.shortName && selectedItem.shortName !== selectedItem.displayName"
                 class="panel-info-line">
                <div class="panel-info-label">英文船名：</div>
                <div class="panel-info-content" :title="selectedItem.shortName">{{selectedItem.shortName}}</div>
            </div>
            <div class="panel-info-line">
                <div class="panel-info-label">MMSI：</div>
                <div class="panel-info-content">{{selectedItem.mmsi}}</div>
            </div>
            <div class="panel-info-line">
                <div class="panel-info-label">航速：</div>
                <div class="panel-info-content">{{speedDesc(selectedItem.sog)}}</div>
            </div>
            <div><span class="text-secondary">AIS船位更新于</span>{{selectedItem.time | timeDesc}}</div>
            <b-form-checkbox v-model="showThermo">显示历史航迹热点</b-form-checkbox>
            <b-form-checkbox v-model="showBerth">显示历史停泊点</b-form-checkbox>
            <b-form-checkbox v-model="showTrail">显示指定日期航迹</b-form-checkbox>
            <div class="d-flex align-items-center">
                <vue-date-picker class="info-date-picker" v-model="startTrailDate" placeholder="请选择起始日期"
                                 :append-to-body="false" :disabled="!showTrail" :clearable="false"
                                 :disabled-date="date => date > endTrailDate"/>
                <div class="ml-1">0点至</div>
            </div>
            <div class="d-flex align-items-center">
                <vue-date-picker class="info-date-picker" v-model="endTrailDate" placeholder="请选择结束日期"
                                 :append-to-body="false" :disabled="!showTrail" :clearable="false"
                                 :disabled-date="date => date < startTrailDate || date > new Date()"/>
                <div class="ml-1">24点</div>
            </div>
            <vue-slider ref="trailSlider" v-model="trailPos" :adsorb="true" :included="true"
                        @change="onTrailPosChanged" @drag-start="onTrailPosDragStart" @drag-end="onTrailPosDragEnd"
                        :data="trailData" :min="trailData[0] || 0" :max="trailData[trailData.length - 1] || 100"
                        :disabled="!showTrail || !trailData.length" :tooltip-formatter="trailPosTooltip"></vue-slider>
            <b-form-checkbox v-model="washTrail">过滤航迹中的跳点</b-form-checkbox>
        </div>
        <div v-if="!compact && distanceText" ref="distanceText" class="distance-text">{{distanceText}}</div>
        <div class="map-menu-bar">
            <div ref="search" class="search-input">
                <input ref="searchInput" class="form-control" v-model="searchInput" @focus="showSearchResult = true">
                <font-awesome-icon icon="search" size="lg" color="dimgray" class="search-icon"/>
                <font-awesome-icon icon="times" size="lg" class="clear-icon"
                                   :class="{'no-pointer-events': !canClearSearch}"
                                   @click="searchClear()" :color="canClearSearch ? 'blue' : 'lightgray'"/>
                <div class="dropdown-menu w-100" style="top: unset" :class="{show: showSearchOptions}">
                    <div v-if="!searching && searchResultKey === searchInput && !searchResult.length"
                         class="text-center">
                        没有匹配的船舶或标绘
                    </div>
                    <template v-else>
                        <template v-if="searchResult.length && searchResultKey === searchInput">
                            <a class="dropdown-item d-flex align-items-center justify-content-between"
                               href="javascript:void(0)" v-for="item in searchResult" @click="selectSearchResult(item)">
                                <div v-html="highlightText(item.displayName, item.keyword)"></div>
                                <div class="text-black-50 form-inline">
                                    <div v-if="item.class === 'vessel'" class="mr-3">
                                        MMSI:<span
                                        :class="{'text-primary' : item.keyword && (item.mmsi || item.id) == item.keyword}">{{item.mmsi || item.id}}</span>
                                    </div>
                                    <div>{{item.type}}</div>
                                </div>
                            </a>
                        </template>
                        <div v-else class="text-center w-100">
                            <b-spinner small label="Small Spinner"></b-spinner>
                        </div>
                    </template>
                </div>
            </div>
            <div ref="route" class="route-panel">
                <div ref="startRoute" class="flex-1 dropdown" style="max-width: 120px">
                    <input :class="startError ? 'text-danger' : ''" ref="routeStartInput" placeholder="输入始发港"
                           class="form-control route-input" v-model="startText" @focus="focusStart()">
                    <div class="dropdown-menu route-dropdown" :class="{show: showStartItems && startItems.length > 0}">
                        <a class="dropdown-item" href="javascript:void(0)" v-for="item in startItems"
                           @click="selectStartItem(item)">
                            <div class="d-flex align-items-center" v-text="item"></div>
                        </a>
                    </div>
                    <div class="dropdown-menu" :class="{show: showStartItems && startItems.length === 0}">
                        <a class="dropdown-item" href="javascript:void(0)">
                            <div class="d-flex align-items-center">
                                没有找到“<span v-text="startText" class="text-danger"></span>”
                            </div>
                        </a>
                    </div>
                </div>
                →
                <div ref="targetRoute" class="flex-1 dropdown mr-1" style="max-width: 120px">
                    <input :class="targetError ? 'text-danger' : ''" ref="routeTargetInput" placeholder="输入目的港"
                           class="form-control route-input" v-model="targetText" @focus="focusTarget()">
                    <div class="dropdown-menu route-dropdown"
                         :class="{show: showTargetItems && targetItems.length > 0}">
                        <a class="dropdown-item" href="javascript:void(0)" v-for="item in targetItems"
                           @click="selectTargetItem(item)">
                            <div class="d-flex align-items-center" v-text="item"></div>
                        </a>
                    </div>
                    <div style="left: 196px;" class="dropdown-menu"
                         :class="{show: showTargetItems && targetItems.length === 0}">
                        <a class="dropdown-item" href="javascript:void(0)">
                            <div class="d-flex align-items-center">
                                没有找到“<span v-text="targetText" class="text-danger"></span>”
                            </div>
                        </a>
                    </div>
                </div>
                <input type="number" v-model="maxWeight" class="form-control flex-1 mr-1"
                       style="max-width: 120px" placeholder="输入吨位"/>
                <div ref="throughPositions" class="flex-1" style="max-width: 220px">
                    <multi-port-selector type="throughPositions" v-model="waypoints" placeholder="输入途径点"/>
                </div>
                <button class="btn btn-primary ml-2" @click="queryRoute()">查看</button>
                <button class="btn btn-secondary ml-1" @click="clearRoute()">清空</button>
            </div>
            <div class="config-buttons">
                <button class="btn btn-success tools-vessel-filter" @click="vesselFilter()">
                    船舶过滤
                    <b-icon v-if="!areFiltersDefault" class="ml-1 text-warning" icon="exclamation-triangle-fill"/>
                </button>
                <b-dropdown class="tools-dropdown" block variant="info" menu-class="w-100" text="工具箱">
                    <b-dropdown-form v-if="canEditPointPlot" @click="clickNewPlot($event)" @change="newPlotChange()">
                        <b-form-checkbox v-model="newPlot">新建点标绘</b-form-checkbox>
                    </b-dropdown-form>
                    <b-dropdown-form v-if="canEditLinePlot"
                                     @click="clickNewLinePlot($event)" @change="newLinePlotChange()">
                        <b-form-checkbox v-model="newLinePlot">新建线标绘</b-form-checkbox>
                    </b-dropdown-form>
                    <b-dropdown-form v-if="canEditRectPlot"
                                     @click="clickNewRectPlot($event)" @change="newRectPlotChange()">
                        <b-form-checkbox v-model="newRectPlot">新建矩形标绘</b-form-checkbox>
                    </b-dropdown-form>
                    <b-dropdown-divider v-if="canEditPointPlot || canEditLinePlot || canEditRectPlot">
                    </b-dropdown-divider>
                    <b-dropdown-form @click="clickMeasureDistance($event)" @change="measureDistanceChange()">
                        <b-form-checkbox v-model="measureDistance">测量距离</b-form-checkbox>
                    </b-dropdown-form>
                    <b-dropdown-form @click="clickMeasureArea($event)" @change="measureAreaChange()">
                        <b-form-checkbox v-model="measureArea">测量面积</b-form-checkbox>
                    </b-dropdown-form>
                    <b-dropdown-form @click="clickMeasureWeight($event)" @change="measureWeightChange()">
                        <b-form-checkbox v-model="measureWeight">量算吨位</b-form-checkbox>
                    </b-dropdown-form>
                </b-dropdown>
                <b-dropdown class="marker-dropdown" block variant="primary" menu-class="w-100" :text="markerTypesDesc">
                    <b-dropdown-form v-for="item in markerTypes" :key="item.name"
                                     @click="clickMarkerType($event, item)" @change="markerTypeChange(item)">
                        <b-form-checkbox v-model="item.value">{{item.name}}</b-form-checkbox>
                    </b-dropdown-form>
                </b-dropdown>
            </div>
            <a v-if="fullscreenUrl" target="_blank" class="full-screen-button" :href="fullscreenUrl">
                <button class="btn btn-outline-primary semi-transparent-btn">
                    <font-awesome-icon icon="expand"/>&nbsp;全屏
                </button>
            </a>
        </div>
        <div v-if="zoomOutVessels || !vesselCount && vesselFilters.type !== 'none'" class="vessel-display-status">
            <span v-if="zoomOutVessels">放大地图显示船舶符号</span>
            <span v-else>当前区域没有符合要求的船舶</span>
        </div>
        <div v-if="tooltip" v-html="tooltipHtml"
             class="map-tooltip" :style="{left: tooltipX + 'px', top: tooltipY + 'px'}">
        </div>
        <edit-plot-dialog ref="editPlotDlg"/>
        <edit-line-plot-dialog ref="editLinePlotDlg"/>
        <edit-rect-plot-dialog ref="editRectPlotDlg"/>
        <vessel-filter-dialog ref="vesselFilterDlg"/>
        <measure-weight-dialog ref="measureWeightDlg"/>
    </div>
</template>

<script>
import * as urlUtils from "@/utils/url";
import User from "@/services/user";
import * as moment from 'moment';
import * as $ from 'jquery';
import OpenLayersMap from 'ol/Map';
import {defaults as defaultInteractions, Translate} from 'ol/interaction';
import {defaults as defaultControls} from 'ol/control';
import View from 'ol/View';
import {fromLonLat, toLonLat, bd09togcj02, wgs84togcj02, mercator2LatLng} from '@/olMap/coordUtils';
import {fromLonLat as olFromLonLat, toLonLat as olToLonLat, getPointResolution} from 'ol/proj';
import Units from 'ol/proj/Units';
import ScaleLineControl from '@/olMap/control/ScaleLine';
import MapTypeControl from '@/olMap/control/MapType';
import RoadTypeControl from '@/olMap/control/RoadType';
import {getLayers} from '@/olMap/mapLayers';
import Plot from '@/services/plot';
import Port from "@/services/port";
import Feature from 'ol/Feature';
import VectorLayer from "ol/layer/Vector";
import HeatmapLayer from "ol/layer/Heatmap";
import VectorSource from "ol/source/Vector";
import {Style, Icon, Text, Fill, Stroke, Circle} from "ol/style";
import {Point, LineString, Polygon} from 'ol/geom';
import Overlay from 'ol/Overlay';
import * as olExtent from 'ol/extent';
import Collection from 'ol/Collection';
import Draw, {createBox} from 'ol/interaction/Draw';
import {getArea, getLength} from 'ol/sphere';
import {unByKey} from 'ol/Observable';
import {
    selectedStyle, createGroupStyle, createPlotStyle, createVesselStyle, createLabelLineStyle, createLabelStyle,
    createFeature, createThermoFeature, createBerthFeature, trailFeatures, plotDisplayName, linePlotDisplayName,
    rectPlotDisplayName, vesselDisplayName, labelDirections, getLabelItem, getPlotLabelItem, getLabelRect,
    placeLabelItems, initPlotTypes, plotTypes, getRectCoordinates, svg, trailPointToolTip,
} from '@/olMap/featureUtils';
import * as Html from '@/utils/html';
import * as MsgBox from "@/utils/msgBox"
import _ from "lodash";
import EditPlotDialog from "@/components/EditPlotDialog";
import VesselFilterDialog from "@/components/VesselFilterDialog";
import MeasureWeightDialog from "@/components/MeasureWeightDialog";
import EditLinePlotDialog from "@/components/EditLinePlotDialog";
import EditRectPlotDialog from "@/components/EditRectPlotDialog";
import MultiPortSelector from "@/components/MultiPortSelector.vue";

export default {
    name: "SearchMap",
    components: {
        MultiPortSelector,
        EditPlotDialog, EditLinePlotDialog, EditRectPlotDialog, MeasureWeightDialog, VesselFilterDialog
    },
    data() {
        return {
            fullscreenUrl: "",
            plotTypes: [],
            mapHeight: "100vh",
            compact: false,
            markerTypes: [],
            tooltip: "",
            tooltipX: 0,
            tooltipY: 0,
            searchInput: null,
            searching: 0,
            showSearchResult: false,
            selectedItem: null,
            searchResultKey: null,
            searchResult: [],
            startError: false,
            targetError: false,
            startText: "",
            targetText: "",
            maxWeight: null,
            waypoints: [],
            distanceText: "",
            startItems: [],
            targetItems: [],
            showStartItems: false,
            showTargetItems: false,
            showTrail: false,
            washTrail: true,
            showThermo: false,
            showBerth: false,
            newPlot: false,
            newLinePlot: false,
            newRectPlot: false,
            startTrailDate: new Date(moment().startOf("day").subtract(6, "days").valueOf()),
            endTrailDate: new Date(moment().startOf("day").valueOf()),
            zoomOutVessels: false,
            vesselCount: 0,
            measureDistance: false,
            measureArea: false,
            measureWeight: false,
            vesselFilters: {},
            delegation: null,
            delegationMmsi: null,
            trailPos: 0,
            trailData: [],
        };
    },
    computed: {
        speedDesc() {
            return speed => (Number.isFinite(+speed) && +speed < 102.3) ? `${+(+speed.toFixed(2))}节` : "——";
        },
        user() {
            return User.user;
        },
        isAdmin() {
            return User.user && User.user.admin;
        },
        canEditPointPlot() {
            return !!this.plotTypes.find(
                x => x.category === "point" && (this.isAdmin ? x.adminEditable : x.userEditable)
            );
        },
        canEditLinePlot() {
            return !!this.plotTypes.find(
                x => x.category === "line" && (this.isAdmin ? x.adminEditable : x.userEditable)
            );
        },
        canEditRectPlot() {
            return !!this.plotTypes.find(
                x => x.category === "rect" && (this.isAdmin ? x.adminEditable : x.userEditable)
            );
        },
        canClearSearch() {
            return this.searchInput || this.selectedItem || this.districtBoundary;
        },
        tooltipHtml() {
            return Html.format(this.tooltip);
        },
        markerTypesDesc() {
            let names = this.showingMarkerTypes();
            if (this.markerTypes.length > 0 && names.length === this.markerTypes.length) {
                return "显示所有标绘";
            } else if (names.length > 0) {
                let result = "显示" + names.join("，");
                if (result.length > 8) {
                    return `显示${names[0]}等标绘`;
                }
                return result;
            } else {
                return "不显示标绘";
            }
        },
        showSearchOptions() {
            return this.showSearchResult && (this.searching || this.searchResult.length > 0
                || this.searchInput && !this.isBaiduLocate(this.searchInput)
                && !this.isLonLatLocate(this.searchInput));
        },
        highlightText(text, keyword) {
            return (text, keyword) => Html.highlight(text, keyword);
        },
        showVesselPanel() {
            return this.selectedItem && this.selectedItem.class === "vessel";
        },
        areFiltersDefault() {
            return (this.vesselFilters.type || "all") === "all"
                && !this.vesselFilters.minWeight && !this.vesselFilters.maxWeight
                && (this.vesselFilters.within || 72) === 72
                && (this.vesselFilters.include || []).length === 0
                && (this.vesselFilters.exclude || []).length === 0;
        },
        momentDate(date) {
            return date => moment(date).format("YYYY年MM月DD日");
        },
    },
    methods: {
        trailPosTooltip: function (value) {
            return moment(value).format("M-D H:m");
        },
        onTrailPosDragStart: function () {
            this.trailSliderDragging = true;
            this.onTrailPosChanged();
        },
        onTrailPosDragEnd: function () {
            this.trailSliderDragging = false;
        },
        onTrailPosChanged: function () {
            if (this.map && this.selectedItem && this.selectedItem.trail) {
                let point = this.selectedItem.trail.find(x => x.time === this.trailPos);
                if (point) {
                    this.trailHighlightTime = point.time;
                    let coordinate = fromLonLat([point.lon, point.lat]);
                    let pixel = this.map.getPixelFromCoordinate(coordinate);
                    let size = this.map.getSize();
                    if (pixel && size && pixel[0] >= 80 && pixel[0] <= size[0] - 300
                        && pixel[1] >= 80 && pixel[1] <= size[1] - 80) {
                        this.updateSelectedVesselLayer();
                    } else {
                        this.map.getView().setCenter(coordinate);
                    }
                }
            }
        },
        focusStart: async function () {
            await this.queryStartItems();
            this.showStartItems = true;
        },
        focusTarget: async function () {
            await this.queryTargetItems();
            this.showTargetItems = true;
        },
        queryStartItems: async function () {
            this.startItems = await Port.multiWords(this.startText);
        },
        queryTargetItems: async function () {
            this.targetItems = await Port.multiWords(this.targetText);
        },
        selectStartItem: function (item) {
            this.showStartItems = false;
            this.startText = item;
            this.$refs.routeStartInput.blur();
        },
        selectTargetItem: function (item) {
            this.showTargetItems = false;
            this.targetText = item;
            this.$refs.routeTargetInput.blur();
        },
        clearRoute: function () {
            this.startText = "";
            this.targetText = "";
            this.maxWeight = null;
            this.waypoints = [];
            this.distanceText = "";
            this.minDistanceTextSize = null;
            urlUtils.setParams({start: null, target: null, max_weight: null, waypoints: null});
            this.setVesselRoute([], [], [], "");
        },
        queryRoute: async function () {
            if (!this.startText) {
                MsgBox.alert(this, "请填写始发港");
                return;
            }
            if (!this.targetText) {
                MsgBox.alert(this, "请填写目的港");
                return;
            }

            urlUtils.setParams({
                start: this.startText,
                target: this.targetText,
                max_weight: this.maxWeight ? this.maxWeight.toString() : "",
                waypoints: this.waypoints.join(),
            });

            this.startError = false;
            this.targetError = false;
            let route = await Plot.route(this.startText, this.targetText, this.maxWeight, this.waypoints.join());
            this.startError = route.startError || Array.isArray(route.startErrors) && route.startErrors.find(x => x);
            this.targetError = route.targetError || Array.isArray(route.targetErrors) && route.targetErrors.find(x => x);
            this.setVesselRoute(route.plots, route.rects, route.throughPlots, route.distanceText);
        },
        showingMarkerTypes() {
            return this.markerTypes.filter(x => x.value).map(x => x.name);
        },
        showingLinePlotTypes() {
            return this.markerTypes.filter(x => x.value && x.category === "line").map(x => x.name);
        },
        showingRectPlotTypes() {
            return this.markerTypes.filter(x => x.value && x.category === "rect").map(x => x.name);
        },
        clickMarkerType(e, item) {
            if (e && e.target && e.target.className) {
                if (e.target.className.indexOf("b-dropdown-form") >= 0
                    || e.target.className.indexOf("custom-checkbox") >= 0) {
                    item.value = !item.value;
                    this.markerTypeChange(item);
                }
            }
        },
        markerTypeChange(item) {
            this.editIdSet.clear();
            this.loadPlots();
            this.updateLinePlotLayer();
            this.updateRectPlotLayer();
            let types = this.showingMarkerTypes().join();
            urlUtils.setParams({plot_types: types});
        },
        clickNewPlot(e) {
            if (e && e.target && e.target.className) {
                if (e.target.className.indexOf("b-dropdown-form") >= 0
                    || e.target.className.indexOf("custom-checkbox") >= 0) {
                    this.newPlot = !this.newPlot;
                    this.newPlotChange();
                }
            }
        },
        newPlotChange() {
            this.newLinePlot = false;
            this.newRectPlot = false;
            this.measureDistance = false;
            this.measureArea = false;
            this.measureWeight = false;
            this.measureChange();
        },
        clickNewLinePlot(e) {
            if (e && e.target && e.target.className) {
                if (e.target.className.indexOf("b-dropdown-form") >= 0
                    || e.target.className.indexOf("custom-checkbox") >= 0) {
                    this.newLinePlot = !this.newLinePlot;
                    this.newLinePlotChange();
                }
            }
        },
        newLinePlotChange() {
            this.newPlot = false;
            this.newRectPlot = false;
            this.measureDistance = false;
            this.measureArea = false;
            this.measureWeight = false;
            this.measureChange();
        },
        clickNewRectPlot(e) {
            if (e && e.target && e.target.className) {
                if (e.target.className.indexOf("b-dropdown-form") >= 0
                    || e.target.className.indexOf("custom-checkbox") >= 0) {
                    this.newRectPlot = !this.newRectPlot;
                    this.newRectPlotChange();
                }
            }
        },
        newRectPlotChange() {
            this.newPlot = false;
            this.newLinePlot = false;
            this.measureDistance = false;
            this.measureArea = false;
            this.measureWeight = false;
            this.measureChange();
        },
        clickMeasureDistance(e) {
            if (e && e.target && e.target.className) {
                if (e.target.className.indexOf("b-dropdown-form") >= 0
                    || e.target.className.indexOf("custom-checkbox") >= 0) {
                    this.measureDistance = !this.measureDistance;
                    this.measureDistanceChange();
                }
            }
        },
        measureDistanceChange() {
            this.newPlot = false;
            this.newLinePlot = false;
            this.newRectPlot = false;
            this.measureArea = false;
            this.measureWeight = false;
            this.measureChange();
        },
        clickMeasureArea(e) {
            if (e && e.target && e.target.className) {
                if (e.target.className.indexOf("b-dropdown-form") >= 0
                    || e.target.className.indexOf("custom-checkbox") >= 0) {
                    this.measureArea = !this.measureArea;
                    this.measureAreaChange();
                }
            }
        },
        measureAreaChange() {
            this.newPlot = false;
            this.newLinePlot = false;
            this.newRectPlot = false;
            this.measureDistance = false;
            this.measureWeight = false;
            this.measureChange();
        },
        clickMeasureWeight(e) {
            if (e && e.target && e.target.className) {
                if (e.target.className.indexOf("b-dropdown-form") >= 0
                    || e.target.className.indexOf("custom-checkbox") >= 0) {
                    this.measureWeight = !this.measureWeight;
                    this.measureWeightChange();
                }
            }
        },
        measureWeightChange() {
            this.newPlot = false;
            this.newLinePlot = false;
            this.newRectPlot = false;
            this.measureDistance = false;
            this.measureArea = false;
            this.measureChange();
        },
        measureChange() {
            this.endMeasure();
            this.updateRectDraw();
            if (this.measureDistance) {
                this.startMeasure("line");
            } else if (this.measureArea) {
                this.startMeasure("area");
            } else if (this.measureWeight) {
                this.startMeasure("area");
            }
        },
        drawLinePlot() {
            this.draw = new Draw({
                source: this.measureSource,
                type: 'LineString',
            });
            this.map.addInteraction(this.draw);
            this.draw.on('drawstart', e => {
                this.drawingFeature = e.feature;
            });

            this.draw.on('drawend', () => {
                setTimeout(async () => {
                    if (this.drawingFeature) {
                        let geom = this.drawingFeature.getGeometry();
                        if (geom) {
                            let points = geom.getCoordinates().map(x => toLonLat(x));
                            let plot = Object.assign({}, this.redrawingLinePlot, {points});
                            setTimeout(() => this.modifyLinePlot(plot));
                        }
                        this.measureSource.removeFeature(this.drawingFeature);
                        this.drawingFeature = null;
                    }
                    this.newLinePlot = false;
                    this.endMeasure();
                });
            });
        },
        drawRectPlot() {
            this.draw = new Draw({
                source: this.measureSource,
                type: 'Circle',
                geometryFunction: createBox(),
            });
            this.map.addInteraction(this.draw);
            this.draw.on('drawstart', e => {
                this.drawingFeature = e.feature;
            });

            this.draw.on('drawend', () => {
                setTimeout(() => {
                    if (this.drawingFeature) {
                        let geom = this.drawingFeature.getGeometry();
                        if (geom) {
                            let extent = geom.getExtent();
                            if (Array.isArray(extent) && extent.length >= 4) {
                                let lonLat1 = toLonLat([extent[0], extent[1]]);
                                let lonLat2 = toLonLat([extent[2], extent[3]]);
                                let plot = Object.assign({}, this.redrawingRectPlot, {
                                    minLon: Math.min(lonLat1[0], lonLat2[0]),
                                    maxLon: Math.max(lonLat1[0], lonLat2[0]),
                                    minLat: Math.min(lonLat1[1], lonLat2[1]),
                                    maxLat: Math.max(lonLat1[1], lonLat2[1]),
                                });
                                this.modifyRectPlot(plot);
                            }
                        }
                        this.measureSource.removeFeature(this.drawingFeature);
                        this.drawingFeature = null;
                    }
                    this.newRectPlot = false;
                    this.endMeasure();
                });
            });
        },
        updateRectDraw() {
            if (this.newLinePlot) {
                this.drawLinePlot();
            } else if (this.newRectPlot) {
                this.drawRectPlot();
            }
        },
        async loadPlots(force) {
            if (this.map) {
                let types = this.showingMarkerTypes();
                if (force || types.length > 0 || this.editIdSet.size > 0) {
                    let zoom = this.map.getView().getZoom();
                    let resolution = this.map.getView().getResolutionForZoom(zoom);
                    let size = this.map.getSize().map(x => x + 50);
                    let extent = this.map.getView().calculateExtent(size);
                    let params = {
                        types: types,
                        zoom: zoom,
                        resolution: resolution,
                        rect: extent.slice(0, 4),
                        system: "gcj02",
                        include: Array.from(this.editIdSet),
                        exclude: this.vesselRoute.map(x => x.id).filter(x => x),
                    };
                    let now = new Date().getTime();
                    if (force || now - (this.lastParamsTime || 0) > 60000
                        || JSON.stringify(params) !== JSON.stringify(this.lastParams)) {
                        this.lastParams = params;
                        this.lastParamsTime = now;
                        let result = await Plot.rect(params);
                        if (JSON.stringify(params) === JSON.stringify(this.lastParams)) {
                            this.groups = result.groups || [];
                            this.groups.forEach((g, index) => {
                                g.coordinate = [g.x, g.y];
                                g.id = index;
                            });
                            this.plots = result.plots || [];
                            this.plots.forEach(p => {
                                p.coordinate = [p.x, p.y];
                                p.aliases = p.aliases || [];
                                p.displayName = plotDisplayName(p);
                            });
                            this.linePlots = result.linePlots || [];
                            this.linePlots.forEach(x => x.coordinates = x.points.map(x => fromLonLat(x)));
                            this.rectPlots = result.rectPlots || [];
                            this.rectPlots.forEach(x => x.coordinates = getRectCoordinates(x));
                            this.updatePlots();
                        }
                    }
                } else {
                    this.plots = [];
                    this.groups = [];
                    this.lastParams = null;
                    this.updatePlots();
                }
            } else {
                setTimeout(() => this.loadPlots(force), 100);
            }
        },
        updatePlots() {
            this.updateGroupLayer();
            this.updatePlotLayer();
            if (this.isSelectedPlot(this.selectedItem)) {
                this.selectPlot(this.selectedItem.id, true);
            }
            this.updateLinePlotLayer();
            this.updateRectPlotLayer();
        },
        updateGroupLayer() {
            let features = this.groups.map(g => this.createGroupFeature(g));
            this.groupLayer.setSource(new VectorSource({features: features}));
        },
        isSelectedPlot(plot) {
            return this.selectedItem && this.selectedItem.class === "plot" && plot.id === this.selectedItem.id;
        },
        isSelectedVessel(vessel) {
            return this.selectedItem && this.selectedItem.class === "vessel" && vessel.mmsi === this.selectedItem.id;
        },
        updatePlotLayer() {
            let occupiedRects = (this.selectedOccupiedRects || []).slice();
            let plotLabelItemsCursor = [];
            let plotLabelItemsNormal = [];
            this.plots = this.plots || [];
            let plots = this.plots.filter(p => !this.isSelectedPlot(p));
            plots.forEach(p => {
                let pixel = this.map.getPixelFromCoordinate(p.coordinate);
                if (pixel) {
                    occupiedRects.push([pixel[0] - 7, pixel[1] - 22, pixel[0] + 7, pixel[1]]);
                }
                let labelItem = getPlotLabelItem(p);
                if (labelItem) {
                    if (p.cursor || p.cursor === 0) {
                        plotLabelItemsCursor.push(labelItem);
                    } else {
                        plotLabelItemsNormal.push(labelItem);
                    }
                }
            });

            let vesselLabelItems = [];
            this.vessels = this.vessels || [];
            let vessels = this.vessels.filter(v => !this.isSelectedVessel(v));
            vessels.forEach(v => {
                let pixel = this.map.getPixelFromCoordinate(v.coordinate);
                if (pixel) {
                    occupiedRects.push([pixel[0] - 7, pixel[1] - 7, pixel[0] + 7, pixel[1] + 7]);
                }
                let labelItem = getLabelItem(v);
                if (labelItem) {
                    vesselLabelItems.push(labelItem);
                }
            });

            plotLabelItemsCursor = placeLabelItems(plotLabelItemsCursor, this.map, occupiedRects);
            plotLabelItemsNormal = placeLabelItems(plotLabelItemsNormal, this.map, occupiedRects);
            vesselLabelItems = placeLabelItems(vesselLabelItems, this.map, occupiedRects);

            let features = [];
            let cursorFeatures = [];
            plots.forEach(p => {
                if (p.cursor || p.cursor === 0) {
                    let labelItem = plotLabelItemsCursor.find(l => l.plot && l.id === p.id);
                    let direction = labelItem && labelItem.direction;
                    cursorFeatures.push(this.createPlotFeature(p, direction));
                } else {
                    let labelItem = plotLabelItemsNormal.find(l => l.plot && l.id === p.id);
                    let direction = labelItem && labelItem.direction;
                    features.push(this.createPlotFeature(p, direction));
                }
            });
            this.plotLayer.setSource(new VectorSource({features: features}));
            this.plotCursorLayer.setSource(new VectorSource({features: cursorFeatures}));

            features = [];
            vessels.forEach(v => {
                let feature = this.createVesselOutlineFeature(v);
                if (feature) {
                    features.push(feature);
                }
            });
            vessels.forEach(v => {
                let feature = this.createVesselFeature(v);
                features.push(feature);
                let tooltip = feature.get("tooltip");
                let labelItem = vesselLabelItems.find(l => l.vessel && l.id === v.mmsi);
                if (labelItem) {
                    let angle = labelItem.direction.angle;
                    features.push(createFeature(v.coordinate, createLabelLineStyle(angle), {tooltip, vessel: v}));
                    features.push(createFeature(v.coordinate, createLabelStyle(labelItem), {tooltip, vessel: v}));
                }
            });
            this.vesselLayer.setSource(new VectorSource({features: features}));
        },
        updateVesselRouteLayer() {
            let features = [];
            this.vesselRoute.forEach(p => {
                if (p.isStart) {
                    if (p.isError) {
                        features.push(this.createPortFeature(p, "image/marker_start_error_sprite.png"));
                    } else {
                        features.push(this.createPortFeature(p, "image/marker_start_sprite.png"));
                    }
                } else if (p.isTarget) {
                    if (p.isError) {
                        features.push(this.createPortFeature(p, "image/marker_target_error_sprite.png"));
                    } else {
                        features.push(this.createPortFeature(p, "image/marker_target_sprite.png"));
                    }
                } else if (!this.isSelectedPlot(p)) {
                    features.push(this.createPlotFeature(p));
                }
            });
            this.vesselWaypoints.forEach(p => {
                features.push(this.createPortFeature(p, "image/marker_waypoint_sprite.png"));
            })
            this.vesselRouteLayer.setSource(new VectorSource({features: features}));
            features = [];
            this.vesselRouteRects.forEach(item => {
                let coordinates = getRectCoordinates(item);
                let tooltipLines = [`名称：${item.name}`];
                if (item.river) {
                    tooltipLines.push(`河流：${item.river}`, `里程：${item.startCursor}~${item.endCursor}`);
                }
                let feature = new Feature({
                    geometry: new Polygon([coordinates]), routeRect: item, tooltip: tooltipLines.join("\n"),
                });
                let color = "magenta";
                feature.setStyle(new Style({
                    fill: new Fill({color: 'rgba(255, 255, 255, 0.2)'}),
                    stroke: new Stroke({color, width: 1}),
                    text: new Text({
                        text: item.name,
                        font: 'normal 12px sans-serif',
                        placement: 'line',
                        offsetX: 0,
                        offsetY: 0,
                        textAlign: "left",
                        textBaseline: "top",
                        fill: new Fill({color: color}),
                        stroke: new Stroke({color: "white", width: 3}),
                    }),
                }));
                features.push(feature);
            });
            this.vesselRouteRectLayer.setSource(new VectorSource({features: features}));
        },
        updateSelectedPlotLayer() {
            let features = [];
            if (this.selectedItem && this.selectedItem.class === "plot") {
                let coordinate = this.selectedItem.coordinate;
                let direction = labelDirections[0];
                features.push(createFeature(coordinate, selectedStyle));
                features.push(this.createPlotFeature(this.selectedItem));
                let occupiedRects = [];
                let pixel = this.map.getPixelFromCoordinate(coordinate);
                if (pixel) {
                    occupiedRects.push([pixel[0] - 17, pixel[1] - 17, pixel[0] + 17, pixel[1] + 17]);
                }
                let labelItem = getPlotLabelItem(this.selectedItem, true);
                if (labelItem) {
                    occupiedRects.push(getLabelRect(labelItem, direction, this.map));
                }
                this.selectedOccupiedRects = occupiedRects;
            } else if (!this.selectedItem) {
                this.selectedOccupiedRects = [];
            }
            this.selectedPlotLayer.setSource(new VectorSource({features: features}));
        },
        async updateSelectedVesselLayer(noAction) {
            let features = [];
            if (this.selectedItem && this.selectedItem.class === "vessel") {
                let coordinate = this.selectedItem.coordinate;
                let outline = this.createVesselOutlineFeature(this.selectedItem);
                if (outline) {
                    features.push(outline);
                }
                features.push(createFeature(coordinate, selectedStyle));
                features.push(this.createVesselFeature(this.selectedItem));
                let text = this.selectedItem.displayName;
                let occupiedRects = [];
                let pixel = this.map.getPixelFromCoordinate(coordinate);
                if (pixel) {
                    occupiedRects.push([pixel[0] - 17, pixel[1] - 17, pixel[0] + 17, pixel[1] + 17]);
                }
                if (text !== '——') {
                    let direction = labelDirections[0];
                    features.push(createFeature(coordinate, createLabelLineStyle(direction.angle, true, false)));
                    features.push(createFeature(coordinate, createLabelStyle({text, direction}, true, false)));
                    let labelItem = getLabelItem(this.selectedItem, true);
                    if (labelItem) {
                        occupiedRects.push(getLabelRect(labelItem, direction, this.map));
                    }
                }
                if (!noAction && this.showTrail && this.selectedItem) {
                    let mmsi = this.selectedItem.mmsi;
                    let trail = this.selectedItem.trail;
                    let info = this.selectedItem.trailInfo;
                    let from = this.getDateText(this.startTrailDate);
                    let to = this.getDateText(this.endTrailDate);
                    let wash = this.washTrail;
                    const isSamePoint = (x, y) => x && y && moment(x.time).valueOf() === moment(y.time).valueOf();
                    if (!trail || !info || mmsi !== info.mmsi || info.from !== from || info.to !== to || info.wash !== wash
                        || (wash && Array.isArray(trail) && !isSamePoint(trail[0], this.selectedItem))) {
                        this.trailFrom = from;
                        this.trailTo = to;
                        this.trailWash = wash;
                        let result = await Plot.vesselTrail(mmsi, from, to, wash);
                        if (mmsi !== this.selectedItem.mmsi || this.trailFrom !== from || this.trailTo !== to || this.trailWash !== wash) {
                            return;
                        }
                        trail = result && result.trail || [];
                        trail.forEach(x => x.coordinate = fromLonLat([x.lon, x.lat]));
                        this.selectedItem.trail = trail;
                        this.selectedItem.trailInfo = {mmsi, from, to, wash};
                        this.trailData = trail.map(x => x.time).sort((a, b) => a - b);
                        this.trailPos = this.trailData.find(x => x === this.trailPos)
                            || this.trailData[this.trailData.length - 1] || 100;
                    }
                    if (trail) {
                        let highlightPoint = trail.find(x => x.time === this.trailHighlightTime);
                        if (highlightPoint) {
                            let vessel = Object.assign({}, this.selectedItem, highlightPoint);
                            let outline = this.createVesselOutlineFeature(vessel, true);
                            if (outline) {
                                features.push(outline);
                            }
                        }
                        let resolution = this.map.getView().getResolution();
                        features = features.concat(trailFeatures(trail, resolution, this.selectedItem, occupiedRects, this.map, this.trailHighlightTime));
                    }
                }
                this.selectedOccupiedRects = occupiedRects;
            } else if (!this.selectedItem) {
                this.selectedOccupiedRects = [];
            }
            this.selectedVesselLayer.setSource(new VectorSource({features: features}));
        },
        getDateText(date) {
            return date ? moment(date).format("YYYY-MM-DD") : null;
        },
        async updateVesselThermoLayer() {
            let features = [];
            if (this.showThermo && this.selectedItem) {
                let thermo = this.washTrail ? this.selectedItem.thermoWashed : this.selectedItem.thermo;
                let thermoTime = this.washTrail ? this.selectedItem.thermoWashedTime : this.selectedItem.thermoTime;
                let mmsi = this.selectedItem.mmsi;
                if (mmsi && (!thermo || new Date().getTime() - thermoTime > 3600000)) {
                    let result = await Plot.vesselThermo(mmsi, this.washTrail);
                    if (mmsi !== (this.selectedItem && this.selectedItem.mmsi)) {
                        return;
                    }
                    thermo = [];
                    result.forEach(pt => {
                        let coordinate = fromLonLat([pt[0], pt[1]]);
                        let count = +pt[2] || 1;
                        for (let i = 0; i < count; i++) {
                            thermo.push(coordinate);
                        }
                    });
                    if (this.washTrail) {
                        this.selectedItem.thermoWashed = thermo;
                        this.selectedItem.thermoWashedTime = new Date().getTime();
                    } else {
                        this.selectedItem.thermo = thermo;
                        this.selectedItem.thermoTime = new Date().getTime();
                    }
                }
                if (thermo) {
                    features = thermo.map(x => createThermoFeature(x, 1));
                }
            }
            if (!this.showThermo) {
                features = [];
            }
            if (features.length > 0 || this.vesselThermoLayer.getSource().getFeatures().length > 0) {
                let source = this.vesselThermoLayer.getSource();
                source.clear();
                source.addFeatures(features);
            }
            this.vesselThermoLayer.setVisible(features.length > 0);
        },
        async updateVesselBerthLayer() {
            let features = [];
            if (this.showBerth && this.selectedItem) {
                let mmsi = this.selectedItem.mmsi;
                if (mmsi) {
                    let result = await Plot.vesselBerth(mmsi);
                    if (mmsi !== (this.selectedItem && this.selectedItem.mmsi)) {
                        return;
                    }
                    result.forEach(x => x.coordinate = fromLonLat([x.lon, x.lat]));
                    features = result.map(x => createBerthFeature(x.coordinate, mmsi, x));
                }
            }
            if (!this.showBerth) {
                features = [];
            }
            this.vesselBerthLayer.setSource(new VectorSource({features: features}));
        },
        updateLinePlotLayer() {
            let features = [];
            if (this.showingLinePlotTypes().length > 0) {
                let lines = this.linePlots || [];
                if (this.redrawingLinePlot) {
                    let index = lines.findIndex(x => x.id === this.redrawingLinePlot.id);
                    if (index >= 0) {
                        this.redrawingLinePlot = lines[index];
                        lines = lines.slice();
                        lines.splice(index, 1);
                    }
                }
                features = lines.map(x => this.createLinePlotFeature(x));
            }
            this.linePlotLayer.setSource(new VectorSource({features: features}));
        },
        updateRectPlotLayer() {
            let features = [];
            if (this.showingRectPlotTypes().length > 0) {
                let rects = this.rectPlots || [];
                if (this.redrawingRectPlot) {
                    let index = rects.findIndex(x => x.id === this.redrawingRectPlot.id);
                    if (index >= 0) {
                        this.redrawingRectPlot = rects[index];
                        rects = rects.slice();
                        rects.splice(index, 1);
                    }
                }
                features = rects.map(x => this.createRectPlotFeature(x));
            }
            if (this.showingRectPlotTypes().length <= 0) {
                features = [];
            }
            this.rectPlotLayer.setSource(new VectorSource({features: features}));
        },
        getDockColor(dock) {
            if (!dock.unload) {
                return "DarkBlue";
            } else if (!dock.load) {
                return "DarkCyan";
            } else {
                return "DarkViolet";
            }
        },
        createLinePlotFeature(item) {
            let tooltipLines = [`ID：${item.id}`, `名称：${item.name}`, `类型：${item.type}`];
            if (item.berth) {
                tooltipLines.push(`泊位：${item.berth}`);
            }
            if (item.river) {
                tooltipLines.push(`河流：${item.river}`, `里程：${item.cursor}`);
            }
            let feature = new Feature({
                geometry: new LineString(item.coordinates), linePlot: item, tooltip: tooltipLines.join("\n"),
            });
            let color = this.getDockColor(item);
            feature.setStyle(new Style({
                stroke: new Stroke({color, width: 3}),
                text: new Text({
                    text: linePlotDisplayName(item),
                    font: 'normal 12px sans-serif',
                    placement: 'line',
                    textBaseline: "top",
                    fill: new Fill({color: color}),
                    stroke: new Stroke({color: "white", width: 3}),
                }),
            }));
            return feature;
        },
        createRectPlotFeature(item) {
            let tooltipLines = [`名称：${item.name}`, `类型：${item.type}`];
            if (item.river) {
                tooltipLines.push(`河流：${item.river}`, `里程：${item.startCursor}~${item.endCursor}`);
            }
            let feature = new Feature({
                geometry: new Polygon([item.coordinates]), rectPlot: item, tooltip: tooltipLines.join("\n"),
            });
            let color = "navy";
            feature.setStyle(new Style({
                fill: new Fill({color: 'rgba(255, 255, 255, 0.2)'}),
                stroke: new Stroke({color, width: 1}),
                text: new Text({
                    text: rectPlotDisplayName(item),
                    font: 'normal 12px sans-serif',
                    placement: 'line',
                    offsetX: 0,
                    offsetY: 0,
                    textAlign: "left",
                    textBaseline: "top",
                    fill: new Fill({color: color}),
                    stroke: new Stroke({color: "white", width: 3}),
                }),
            }));
            return feature;
        },
        createGroupFeature(g) {
            let tooltip = `${g.size}个${this.showingMarkerTypes().join("或")}`;
            return createFeature(g.coordinate, createGroupStyle(g), {group: g, tooltip: tooltip});
        },
        createPlotFeature(p, direction) {
            let tooltipLines = [`名称：${p.name}`, `类型：${p.type}`];
            if (p.aliases.length > 0) {
                tooltipLines.push(`别名：${p.aliases.join()}`);
            }
            if (p.river) {
                tooltipLines.push(`河流：${p.river}`);
            }
            let hasCursor = (p.cursor || p.cursor === 0);
            if (hasCursor) {
                tooltipLines.push(`里程：${p.cursor}`);
            }
            if (p.extensionRiver && !p.river) {
                tooltipLines.push(`推测河流：${p.extensionRiver}`);
            }
            let hasExtensionCursor = (p.extensionCursor || p.extensionCursor === 0);
            if (hasExtensionCursor && !p.river) {
                tooltipLines.push(`推测里程：${p.extensionCursor}`);
            }
            if (p.extensionOrigin && !p.river) {
                tooltipLines.push(`推测来源：${p.extensionOrigin}`);
            }
            if (p.extensionPort) {
                tooltipLines.push(`所属港口：${p.extensionPort}`);
            }
            if (p.note) {
                tooltipLines.push(`备注：${p.note}`);
            }
            return createFeature(p.coordinate, createPlotStyle(p, direction, hasCursor ? "#dff0d8" : null), {
                plot: p, tooltip: tooltipLines.join("\n")
            });
        },
        createPortFeature(p, imagePath) {
            return createFeature(p.coordinate, new Style({
                image: new Icon({src: imagePath, size: [40, 40], anchor: [0.5, 0.9]})
            }), {plot: p, tooltip: p.name, type: 'port'});
        },
        getVesselTooltip(v, isSelected) {
            let tooltipLines = [
                `MMSI：${v.mmsi || '——'}`,
                !isSelected ? `船名：${v.name || '——'}` : "",
                isSelected ? `中文船名：${v.chnName || '——'}` : "",
                isSelected ? `英文船名：${v.shortName || '——'}` : "",
                `航速：${this.speedDesc(v.sog)}`,
                `航迹向：${v.cog}°`,
                `更新时间：${moment(v.time).format("YYYY-MM-DD HH:mm:ss")}`,
            ].filter(x => x);
            if (Number.isFinite(v.heading)) {
                tooltipLines.push(`船艏向：${v.heading}°`);
            }
            if (v.weight > 0) {
                tooltipLines.push(`吨位：${v.weight}`);
            }
            if (v.length > 0) {
                tooltipLines.push(`船长：${v.length}米`);
            }
            if (v.width > 0) {
                tooltipLines.push(`船宽：${v.width}米`);
            }
            return tooltipLines.join("\n");
        },
        createVesselFeature(v) {
            let isSelected = this.isSelectedVessel(v);
            let style = createVesselStyle(v, isSelected, v.docking ? "DarkViolet" : null);
            return createFeature(v.coordinate, style, {vessel: v, tooltip: this.getVesselTooltip(v, isSelected)});
        },
        createVesselOutlineFeature(v, isHighlight) {
            if (this.getRes && v.dim) {
                let isSelected = this.isSelectedVessel(v);
                let res = this.getRes(v.coordinate);
                let dim = Object.assign({}, v.dim);
                Object.getOwnPropertyNames(dim).forEach(f => dim[f] /= res);
                let center = [
                    Math.ceil(Math.max(dim.port, dim.starboard)) + 1,
                    Math.ceil(Math.max(dim.bow, dim.stern)) + 1,
                ];
                let size = [center[0] * 2, center[1] * 2];
                let offsetX = center[0] - dim.port;
                let offsetY = center[1] - dim.bow;
                let points = [
                    [dim.width / 2, 0],
                    [0, dim.length / 3],
                    [0, dim.length],
                    [dim.width, dim.length],
                    [dim.width, dim.length / 3],
                ].map(p => [p[0] + offsetX, p[1] + offsetY]);
                let fill = v.docking ? "rgba(240, 230, 140, 0.7)"
                    : (isSelected ? "rgba(255, 0, 0, 0.3)" : "rgba(255, 255, 255, 0.3)");
                let stroke = isSelected ? "red" : "black";
                if (isHighlight) {
                    fill = "rgba(0, 255, 255, 0.3)"
                    stroke = "cyan";
                }
                let xml = `<polygon points="${points.map(p => `${p[0]},${p[1]}`).join(" ")}" fill="${fill}" stroke="${stroke}"></polygon>`;
                let head = ((!v.heading || v.sog >= 0.5) ? v.cog : v.heading) || 0;
                let style = new Style({
                    image: new Icon({
                        src: svg(size[0], size[1], xml),
                        imgSize: size,
                        rotation: Math.PI * head / 180,
                    })
                });
                let params = {vessel: v, tooltip: this.getVesselTooltip(v, isSelected)};
                if (isHighlight) {
                    params = {trail: v, tooltip: trailPointToolTip(v)};
                }
                return createFeature(v.coordinate, style, params);
            }
            return null;
        },
        getMeasureAction() {
            if (this.measureDistance) {
                return "测量距离";
            } else if (this.measureArea) {
                return "测量面积";
            } else if (this.measureWeight) {
                return "量算吨位";
            } else {
                return "";
            }
        },
        onPointerMove(e) {
            if (e.dragging) {
                return;
            }

            if (this.measureDistance || this.measureArea || this.measureWeight) {
                if (this.helpTooltipElement && this.helpTooltip) {
                    this.helpTooltipElement.innerHTML =
                        "点击" + (this.drawingFeature ? "继续" : "开始") + this.getMeasureAction();
                    this.helpTooltip.setPosition(e.coordinate);
                    this.helpTooltipElement.classList.remove('hidden');
                }
            } else {
                this.lastPointerPixel = this.map.getEventPixel(e.originalEvent);
                this.updateCursor();
            }
        },
        updateCursor() {
            let cursor = "default";
            let feature = this.updateTooltip();
            if (this.newPlot || this.newRectPlot) {
                cursor = "crosshair";
            } else if (feature) {
                let vessel = feature.get("vessel");
                let pointer = !!feature.get("group")
                    || vessel && vessel.mmsi > 0 && !this.isSelectedVessel(vessel);
                if (!pointer) {
                    let trail = feature.get("trail");
                    pointer = !!trail;
                }
                if (!pointer) {
                    let plot = feature.get("plot");
                    let typeInfo = plotTypes.find(x => plot && plot.id && x.type === plot.type);
                    pointer = !!(typeInfo && (this.isAdmin ? typeInfo.adminEditable : typeInfo.userEditable));
                    if (!this.translating) {
                        if (pointer) {
                            this.translateFeatures.setAt(0, feature);
                        } else {
                            this.translateFeatures.clear();
                        }
                    }
                }
                if (!pointer) {
                    let linePlot = feature.get("linePlot");
                    let typeInfo = plotTypes.find(x => linePlot && linePlot.id && x.type === linePlot.type);
                    pointer = !!(typeInfo && (this.isAdmin ? typeInfo.adminEditable : typeInfo.userEditable));
                }
                if (!pointer) {
                    let rectPlot = feature.get("rectPlot");
                    let typeInfo = plotTypes.find(x => rectPlot && rectPlot.id && x.type === rectPlot.type);
                    pointer = !!(typeInfo && (this.isAdmin ? typeInfo.adminEditable : typeInfo.userEditable));
                }
                cursor = pointer ? "pointer" : "default";
            }
            document.body.style.cursor = cursor;
        },
        updateTooltip() {
            if (this.lastPointerPixel) {
                let feature = this.getTooltipFeature(this.lastPointerPixel);
                if (feature) {
                    this.tooltip = feature.get('tooltip');
                    this.tooltipX = this.lastPointerPixel[0] + 7;
                    this.tooltipY = this.lastPointerPixel[1] + 20;
                } else if (this.tooltip) {
                    this.tooltip = "";
                }
                return feature;
            }
        },
        getTooltipFeature(pixel) {
            let feature = null;
            this.map.forEachFeatureAtPixel(pixel, function (x) {
                feature = feature || (x.get('tooltip') ? x : null);
            }, {hitTolerance: 5});
            return feature;
        },
        async modifyPlot(plot) {
            document.body.style.cursor = "default";
            if (plot.id) {
                plot = await Plot.load(plot.id);
            }
            let result = await this.$refs.editPlotDlg.modal(plot);
            if (result !== void (0)) {
                if (result) {
                    this.editIdSet.add(result.id);
                }
                this.loadPlots(true);
                this.newPlot = false;
                this.lastSearchInput = void (0);
                this.onKeywordChanged();
            }
        },
        async modifyLinePlot(linePlot) {
            document.body.style.cursor = "default";
            let result = await this.$refs.editLinePlotDlg.modal(linePlot);
            if (result === "redraw") {
                this.redrawingLinePlot = linePlot;
                this.updateLinePlotLayer();
                this.newLinePlot = false;
                this.newLinePlotChange();
                this.drawLinePlot();
            } else if (result !== void (0) || this.redrawingLinePlot) {
                this.redrawingLinePlot = null;
                this.newLinePlot = false;
                this.loadPlots(true);
                this.lastSearchInput = void (0);
                this.onKeywordChanged();
            }
        },
        async modifyRectPlot(rectPlot) {
            document.body.style.cursor = "default";
            this.modifyingRectPlot = true;
            let result = await this.$refs.editRectPlotDlg.modal(rectPlot);
            this.modifyingRectPlot = false;
            if (result === "redraw") {
                this.redrawingRectPlot = rectPlot;
                this.updateRectPlotLayer();
                this.newRectPlot = false;
                this.newRectPlotChange();
                this.drawRectPlot();
            } else if (result !== void (0) || this.redrawingRectPlot) {
                this.redrawingRectPlot = null;
                this.newRectPlot = false;
                this.loadPlots(true);
                this.lastSearchInput = void (0);
                this.onKeywordChanged();
            }
        },
        onClick(e) {
            if (e && e.originalEvent && e.originalEvent.button === 0) {
                if (this.newPlot) {
                    let coordinate = this.map.getEventCoordinate(e.originalEvent);
                    let lonLat = toLonLat(coordinate);
                    this.modifyPlot({lon: lonLat[0], lat: lonLat[1]});
                } else if (this.newLinePlot) {
                } else if (this.newRectPlot || this.modifyingRectPlot) {
                } else if (this.measureDistance || this.measureArea || this.measureWeight) {
                } else {
                    let pixel = this.map.getEventPixel(e.originalEvent);
                    let feature = this.getTooltipFeature(pixel);
                    if (feature) {
                        let group = feature.get('group');
                        let vessel = feature.get('vessel');
                        let trail = feature.get('trail');
                        let plot = feature.get('plot');
                        let linePlot = feature.get('linePlot');
                        let rectPlot = feature.get('rectPlot');
                        if (group) {
                            this.zoomIn(group.coordinate);
                        } else if (vessel && vessel.mmsi > 0) {
                            this.selectVessel(vessel.mmsi, true);
                        } else if (trail) {
                            this.trailPos = trail.time;
                            if (this.trailHighlightTime === trail.time) {
                                this.trailHighlightTime = null;
                                this.updateSelectedVesselLayer();
                            } else {
                                this.onTrailPosChanged();
                                if (this.trailHighlightTime === trail.time) {
                                    this.$refs.trailSlider.focus();
                                }
                            }
                            setTimeout(() => this.updateCursor());
                        } else if (plot) {
                            let typeInfo = plotTypes.find(x => plot && plot.id && x.type === plot.type);
                            if (typeInfo && (this.isAdmin ? typeInfo.adminEditable : typeInfo.userEditable)) {
                                this.modifyPlot(plot);
                            }
                        } else if (linePlot) {
                            let typeInfo = plotTypes.find(x => linePlot && linePlot.id && x.type === linePlot.type);
                            if (typeInfo && (this.isAdmin ? typeInfo.adminEditable : typeInfo.userEditable)) {
                                this.modifyLinePlot(linePlot);
                            }
                        } else if (rectPlot) {
                            let typeInfo = plotTypes.find(x => rectPlot && rectPlot.id && x.type === rectPlot.type);
                            if (typeInfo && (this.isAdmin ? typeInfo.adminEditable : typeInfo.userEditable)) {
                                this.modifyRectPlot(rectPlot);
                            }
                        }
                    }
                }
            }
        },
        async onTranslateStart(e) {
            this.translating = true;
        },
        async onTranslateEnd(e) {
            this.translating = false;
            let feature = e.features.item(0);
            let plot = feature && feature.get("plot");
            if (plot) {
                let delta = [e.coordinate[0] - e.startCoordinate[0], e.coordinate[1] - e.startCoordinate[1]];
                let resolution = this.map.getView().getResolution();
                let deltaPixel = [delta[0] / resolution, delta[1] / resolution];
                if (deltaPixel[0] * deltaPixel[0] + deltaPixel[1] * deltaPixel[1] > 100
                    && await MsgBox.confirm(this, `确认要移动标绘“${plot.name}”吗？`)) {
                    let coordinate = [plot.coordinate[0] + delta[0], plot.coordinate[1] + delta[1]];
                    let lonLat = toLonLat(coordinate);
                    if (await Plot.changeCoordinate(plot.id, lonLat[0], lonLat[1])) {
                        this.editIdSet.add(plot.id);
                        this.loadPlots(true);
                    }
                } else {
                    feature.setGeometry(new Point(plot.coordinate));
                }
            }
        },
        zoomIn(center) {
            let view = this.map.getView();
            view.setZoom(view.getZoom() + 1);
            view.setCenter(center);
            setTimeout(() => this.updateTooltip(), 0);
        },
        searchClear() {
            this.searchInput = "";
            this.selectSearchResult(null);
            this.selectBoundary(null);
            setTimeout(() => this.$refs.searchInput.focus());
        },
        isBaiduLocate(str) {
            return /^-?\d+\.?\d*,-?\d+\.?\d*,\d+\.?\d*z$/.test(str);
        },
        isLonLatLocate(str) {
            return /^(wgs84:|gcj02:|baidu09:)-?\d+\.?\d*,-?\d+\.?\d*(,\d+\.?\d*z?)?$/.test(str);
        },
        async onKeywordChanged() {
            if (this.searchInput !== this.lastSearchInput) {
                this.lastSearchInput = this.searchInput;
                let searchInput = (this.searchInput || "").trim();
                if (this.isBaiduLocate(searchInput)) {
                    this.searchResultKey = null;
                    this.searchResult = [];
                    return this.baiduLocate(searchInput);
                } else if (this.isLonLatLocate(searchInput)) {
                    this.searchResultKey = null;
                    this.searchResult = [];
                    return this.lonLatLocate(searchInput);
                }
                let results = null;
                try {
                    this.searching++;
                    results = await Promise.all([
                        searchInput ? Plot.search(searchInput) : Plot.selectHistory(),
                        this.getBoundary(searchInput),
                    ]);
                } finally {
                    this.searching--;
                }
                if (searchInput) {
                    results[0].forEach(x => {
                        if (x.class === "vessel") {
                            x.displayName = vesselDisplayName(x);
                        } else {
                            if (x.category === "line") {
                                x.displayName = linePlotDisplayName(x);
                            } else if (x.category === "rect") {
                                x.displayName = rectPlotDisplayName(x);
                            } else {
                                x.displayName = plotDisplayName(x);
                            }
                        }
                    });
                }
                results[1] = (Array.isArray(results[1]) && results[1].length > 0) ? [{
                    class: "district",
                    id: searchInput,
                    displayName: searchInput,
                    boundary: results[1],
                    type: "行政区划",
                    keyword: searchInput,
                }] : [];
                if (searchInput === this.searchInput || this.searchResultKey !== this.searchInput) {
                    let result = results[1].concat(results[0]);
                    this.searchResultKey = this.searchInput;
                    this.searchResult = result;
                }
            }
        },
        async selectVessel(mmsi, noAction) {
            await this.selectSearchResult({class: "vessel", mmsi}, noAction, false, false, true);
            return !!this.selectedItem;
        },
        async selectPlot(id, noAction) {
            await this.selectSearchResult({class: "plot", id}, noAction);
            return !!this.selectedItem;
        },
        getStringName(name) {
            return (name || name === 0) ? name.toString() : "";
        },
        patchVesselDim(vessel) {
            if (vessel.length > 0 && vessel.width > 0 && vessel.length < 500 && vessel.width < 80
                && vessel.length > vessel.width
                && vessel.length >= (vessel.trail || 0) && vessel.width >= (vessel.left || 0)) {
                vessel.dim = {
                    length: vessel.length,
                    width: vessel.width,
                    bow: vessel.length - (vessel.trail || 0),
                    stern: vessel.trail || 0,
                    port: vessel.left || 0,
                    starboard: vessel.width - (vessel.left || 0),
                }
            }
        },
        onSelectVessel(selected) {
            if (selected) {
                this.patchVesselDim(selected);
                selected.class = "vessel";
                selected.type = "船舶";
                selected.displayName = vesselDisplayName(selected);
                selected.id = selected.mmsi;
                selected.time = new Date(selected.updateTime).getTime();
                let interval = new Date().getTime() - new Date(selected.time).getTime();
                selected.recent = interval < 2 * 3600 * 1000;
                selected.in24h = interval < 24 * 3600 * 1000;
                this.showSearchResult = false;
                this.searchInput = this.getStringName(selected.chnName || selected.shortName || selected.mmsi);
                if (this.selectedItem && selected.type === this.selectedItem.type && selected.id === this.selectedItem.id) {
                    selected.thermo = this.selectedItem.thermo;
                    selected.thermoTime = this.selectedItem.thermoTime;
                    selected.thermoWashed = this.selectedItem.thermoWashed;
                    selected.thermoWashedTime = this.selectedItem.thermoWashedTime;
                }
                urlUtils.setParams({
                    select: `vessel_${selected.mmsi}`,
                    delegation: this.delegationMmsi === selected.mmsi ? this.delegation : null,
                });
            }
        },
        async selectSearchResult(item, noAction, noActionAfterViewPortSet, noLog, manualSelect) {
            let selected = null;
            if (item && item.class === "plot") {
                if (item.category === "line" || item.category === "rect") {
                    this.showSearchResult = false;
                    let coordinates = getRectCoordinates(item);
                    this.setViewport(coordinates, 16);
                    let type = this.markerTypes.find(x => x.category === item.category && x.name === item.type);
                    if (type && !type.value) {
                        type.value = true;
                        this.markerTypeChange(type);
                    }
                } else {
                    selected = await Plot.load(item.id);
                    if (selected) {
                        selected.class = "plot";
                        selected.displayName = plotDisplayName(selected);
                        this.showSearchResult = false;
                        this.searchInput = this.getStringName(selected.name);
                        urlUtils.setParams({select: `plot_${selected.id}`, delegation: null});
                    }
                }
            } else if (item && item.class === "vessel") {
                selected = await Plot.loadVessel(item.mmsi || item.id, false);
                if (selected) {
                    this.onSelectVessel(selected);
                    if (this.washTrail) {
                        selected.keyword = item.keyword;
                        selected.coordinate = fromLonLat([selected.lon, selected.lat]);
                        this.selectedItem = selected;
                        this.updateSelectedPlotLayer();
                        this.updateSelectedVesselLayer(true);
                        selected = await Plot.loadVessel(item.mmsi || item.id, true);
                        this.onSelectVessel(selected);
                    }
                }
            } else if (item && item.class === "district") {
                this.showSearchResult = false;
                this.searchInput = item.displayName;
                this.selectBoundary(item.boundary);
                urlUtils.setParams({select: `district_${item.displayName}`, delegation: null});
            }
            if (selected) {
                selected.keyword = item.keyword;
                selected.coordinate = fromLonLat([selected.lon, selected.lat]);
                let pixel = this.map.getPixelFromCoordinate(selected.coordinate);
                let size = this.map.getSize();
                this.selectedItem = selected;
                if (!noAction && !(noActionAfterViewPortSet && this.viewportSet)
                    || manualSelect && (pixel[0] < 20 || pixel[1] < 20 || pixel[0] > size[0] - 20 || pixel[1] > size[1] - 20)) {
                    let view = this.map.getView();
                    view.setCenter(selected.coordinate);
                    if (!noLog) {
                        Plot.select(selected);
                    }
                }
            } else {
                this.selectedItem = null;
                urlUtils.setParams({select: null, delegation: null});
            }
            this.updateSelectedPlotLayer();
            this.updateSelectedVesselLayer();
            this.updateVesselThermoLayer();
            this.updateVesselBerthLayer();
            this.updatePlotLayer();
        },
        selectBoundary(boundary) {
            let features = [];
            if (Array.isArray(boundary) && boundary.length > 0) {
                let coordinates = boundary.map(b => {
                    let lonLats = b.split(";").map(p => p.split(",").map(x => +x));
                    return lonLats.map(p => olFromLonLat(bd09togcj02(p[0], p[1])));
                });
                features = [new Feature(new Polygon(coordinates))];
                this.districtBoundary = boundary;
                this.setViewport(coordinates.reduce((r, x) => r.concat(x), []));
            } else {
                this.districtBoundary = null;
            }
            this.districtBoundaryLayer.setSource(new VectorSource({features: features}));
        },
        baiduLocate(str) {
            let comps = str.split(",");
            let bd09 = mercator2LatLng({x: +comps[0], y: +comps[1]});
            let gcj02 = bd09togcj02(bd09.lng, bd09.lat);
            let zoom = +comps[2].slice(0, -1);
            let view = this.map.getView();
            view.setCenter(olFromLonLat(gcj02));
            view.setZoom(zoom);
        },
        lonLatLocate(str) {
            let comps = str.split(":");
            let system = comps[0];
            comps = comps[1].split(",");
            let lonLat = [+comps[0], +comps[1]];
            let zoom = parseFloat(comps[2]) || 15;
            switch (system) {
                case "wgs84":
                    lonLat = wgs84togcj02(lonLat[0], lonLat[1]);
                    break;
                case "baidu09":
                    lonLat = bd09togcj02(lonLat[0], lonLat[1]);
                    break;
            }
            let view = this.map.getView();
            view.setCenter(olFromLonLat(lonLat));
            view.setZoom(zoom);
        },
        setVesselRoute(vesselRoute, vesselRouteRects, vesselWaypoints, distanceText) {
            if (this.map) {
                vesselRoute.forEach(x => x.coordinate = fromLonLat([x.lon, x.lat]));
                vesselWaypoints.forEach(x => x.coordinate = fromLonLat([x.lon, x.lat]));
                this.vesselRoute = vesselRoute;
                this.vesselRouteRects = vesselRouteRects;
                this.vesselWaypoints = vesselWaypoints;
                this.distanceText = distanceText || "";
                this.updateVesselRouteLayer();
                if (this.vesselRoute.length > 0) {
                    this.setViewport(this.vesselRoute.concat(this.vesselWaypoints).map(x => x.coordinate));
                }
                this.loadPlots(true);
                this.distanceTextSizeHandler();
            } else {
                setTimeout(() => this.setVesselRoute(vesselRoute, vesselRouteRects, vesselWaypoints, distanceText), 100);
            }
        },
        setViewport(coordinates, maxZoom, maxPadding) {
            if (Array.isArray(coordinates) && coordinates.length > 0) {
                let size = this.getClientSize();
                let px = Number.isFinite(maxPadding) ? maxPadding : Math.max(50, Math.round(size[0] * 0.2));
                let py = Number.isFinite(maxPadding) ? maxPadding : Math.max(50, Math.round(size[1] * 0.2));
                this.map.getView().fit(olExtent.boundingExtent(coordinates), {
                    padding: [py, px, py, px],
                    maxZoom: maxZoom || 11,
                });
                this.viewportSet = true;
            }
        },
        getClientSize() {
            return [
                window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth,
                window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight,
            ];
        },
        async vesselFilter() {
            let result = await this.$refs.vesselFilterDlg.modal(this.getFilters());
            if (result) {
                this.vesselFilters = result;
                Plot.saveVesselFilters(result);
                await this.loadVessels();
            }
        },
        async initVessels() {
            this.vesselFilters = (await Plot.loadVesselFilters()) || {};
            this.vesselFiltersInited = true;
            this.loadVessels();
        },
        getFilters() {
            return {
                type: this.vesselFilters.type || "all",
                minWeight: this.vesselFilters.minWeight || null,
                maxWeight: this.vesselFilters.maxWeight || null,
                within: this.vesselFilters.within || 72,
                include: (this.vesselFilters.include || []).slice(),
                exclude: (this.vesselFilters.exclude || []).slice(),
            };
        },
        async loadVessels(force) {
            if (this.map && this.vesselFiltersInited) {
                let filters = this.getFilters();
                if (force || filters.type !== "none") {
                    let zoom = this.map.getView().getZoom();
                    let resolution = this.map.getView().getResolutionForZoom(zoom);
                    let size = this.map.getSize();
                    let extent = this.map.getView().calculateExtent(size);
                    let rect = extent.slice(0, 4);
                    let params = {filters, zoom, resolution, rect, system: "gcj02"};
                    let now = new Date().getTime();
                    if (force || now - (this.lastVesselParamsTime || 0) > 60000
                        || JSON.stringify(params) !== JSON.stringify(this.lastVesselParams)) {
                        this.lastVesselParams = params;
                        this.lastVesselParamsTime = now;
                        let result = await Plot.vessels(params);
                        if (JSON.stringify(params) === JSON.stringify(this.lastVesselParams)) {
                            this.zoomOutVessels = result.zoomOutVessels;
                            this.vesselCount = result.vessels.length;
                            this.vessels = result.vessels;
                            let now = new Date().getTime();
                            this.vessels.forEach(v => {
                                v.id = v.mmsi;
                                this.patchVesselDim(v);
                                v.coordinate = fromLonLat([v.lon, v.lat]);
                                v.recent = now - v.time < 2 * 3600 * 1000;
                                v.in24h = now - v.time < 24 * 3600 * 1000;
                                v.displayName = vesselDisplayName(v);
                            });
                            this.updatePlotLayer();
                        }
                    }
                } else {
                    this.lastVesselParams = null;
                    this.zoomOutVessels = false;
                    this.vesselCount = 0;
                    this.vessels = [];
                    this.updatePlotLayer();
                }
            } else {
                setTimeout(() => this.loadVessels(force), 100);
            }
        },
        startMeasure(type) {
            this.draw = new Draw({
                source: this.measureSource,
                type: type === 'area' ? 'Polygon' : 'LineString',
                style: new Style({
                    fill: new Fill({color: 'rgba(255, 255, 255, 0.2)'}),
                    stroke: new Stroke({color: 'rgba(0, 0, 0, 0.5)', lineDash: [10, 10], width: 2}),
                    image: new Circle({
                        radius: 5,
                        stroke: new Stroke({color: 'rgba(0, 0, 0, 0.7)'}),
                        fill: new Fill({color: 'rgba(255, 255, 255, 0.2)'}),
                    }),
                }),
            });
            this.map.addInteraction(this.draw);

            this.createMeasureTooltip();
            this.createHelpTooltip();

            let listener;
            this.draw.on('drawstart', e => {
                this.drawingFeature = e.feature;
                listener = this.drawingFeature.getGeometry().on('change', e => {
                    if (this.measureTooltipElement && this.measureTooltip) {
                        let geom = e.target;
                        let output = "";
                        if (geom instanceof Polygon) {
                            output = this.formatArea(geom);
                            this.tooltipCoord = geom.getInteriorPoint().getCoordinates();
                        } else if (geom instanceof LineString) {
                            output = this.formatLength(geom);
                            this.tooltipCoord = geom.getLastCoordinate();
                        }
                        this.measureTooltipElement.innerHTML = output;
                        this.measureTooltip.setPosition(this.tooltipCoord);
                    }
                });
            });

            this.draw.on('drawend', () => {
                let item = {
                    overlay: this.measureTooltip,
                    element: this.measureTooltipElement,
                    feature: this.drawingFeature,
                };
                let measureWeight = this.measureWeight;
                if (this.measureTooltipElement && this.measureTooltip) {
                    this.measureTooltipElement.className = 'ol-tooltip ol-tooltip-static';
                    this.measureTooltip.setOffset([0, -7]);
                    item.areaHtml = this.measureTooltipElement.innerHTML;
                    let inner = $(`<div class="d-flex align-items-center"><div>${item.areaHtml}</div></div>`);
                    let closeButton = $('<div class="ml-1 ol-tooltip-static-close-btn">&times;</div>');
                    closeButton.on('click', () => this.removeMeasureFeature(item));
                    inner.append(closeButton);
                    this.measureTooltipElement.innerHTML = "";
                    this.measureTooltipElement.appendChild(inner[0]);
                    this.measureTooltipElement = null;
                    this.measureTooltip = null;
                }
                this.drawingFeature = null;
                unByKey(listener);
                setTimeout(() => {
                    this.endMeasure();
                    this.measureDistance = this.measureArea = this.measureWeight = false;
                    if (measureWeight) {
                        this.doMeasureWeight(item);
                    }
                });
            });
        },
        removeMeasureFeature(item) {
            item.element && item.element.parentNode.removeChild(item.element);
            item.element = null;
            item.overlay && this.map.removeOverlay(item.overlay);
            item.overlay = null;
            item.feature && this.measureSource.removeFeature(item.feature);
            item.feature = null;
        },
        endMeasure() {
            this.destroyMeasureTooltip();
            this.destroyHelpTooltip();
            this.drawingFeature = null;
            if (this.draw) {
                this.map.removeInteraction(this.draw);
                this.draw = null;
            }
        },
        createHelpTooltip() {
            this.destroyHelpTooltip();
            this.helpTooltipElement = document.createElement('div');
            this.helpTooltipElement.className = 'ol-tooltip hidden';
            this.helpTooltip = new Overlay({
                element: this.helpTooltipElement,
                offset: [15, 0],
                positioning: 'center-left',
                stopEvent: false,
            });
            this.map.addOverlay(this.helpTooltip);
        },
        destroyHelpTooltip() {
            if (this.helpTooltipElement) {
                this.helpTooltipElement.parentNode.removeChild(this.helpTooltipElement);
                this.helpTooltipElement = null;
            }
            if (this.helpTooltip) {
                this.map.removeOverlay(this.helpTooltip);
                this.helpTooltip = null;
            }
        },
        createMeasureTooltip() {
            this.destroyMeasureTooltip();
            this.measureTooltipElement = document.createElement('div');
            this.measureTooltipElement.className = 'ol-tooltip ol-tooltip-measure';
            this.measureTooltip = new Overlay({
                element: this.measureTooltipElement,
                offset: [0, -15],
                positioning: 'bottom-center',
                stopEvent: false,
            });
            this.map.addOverlay(this.measureTooltip);
        },
        destroyMeasureTooltip() {
            if (this.measureTooltipElement) {
                this.measureTooltipElement.parentNode.removeChild(this.measureTooltipElement);
                this.measureTooltipElement = null;
            }
            if (this.measureTooltip) {
                this.map.removeOverlay(this.measureTooltip);
                this.measureTooltip = null;
            }
        },
        formatLength(line) {
            let length = getLength(line);
            return length > 100
                ? ((Math.round(length / 1000 * 100) / 100) + '公里')
                : ((Math.round(length * 100) / 100) + '米');
        },
        formatArea(polygon) {
            let area = getArea(polygon);
            return area > 10000
                ? (Math.round(area / 1000000 * 100) / 100) + ' ' + '公里<sup>2</sup>'
                : (Math.round(area * 100) / 100) + ' ' + '米<sup>2</sup>';
        },
        async doMeasureWeight(item) {
            if (item.feature) {
                let polygon = item.feature.getGeometry();
                await this.$refs.measureWeightDlg.modal(polygon, item.areaHtml);
                this.removeMeasureFeature(item);
            }
        },
        async initPlotTypes() {
            await initPlotTypes();
            this.plotTypes = plotTypes;
            this.markerTypes = plotTypes.map(x => ({name: x.type, value: false, category: x.category}));
        },
        async initBaiduMap() {
            if (typeof BMapGL === typeof void (0)) {
                await new Promise(resolve => {
                    window.bmapgl_initialize = resolve;
                    let script = document.createElement("script");
                    script.src = "https://api.map.baidu.com/api?v=1.0&type=webgl&ak=CVff1TFGSvbxDDyq8NkyeuEWvVftVftg&callback=bmapgl_initialize";
                    document.body.appendChild(script);
                });
            }
            this.mapBoundary = new BMapGL.Boundary();
        },
        async getBoundary(name) {
            if (name) {
                if (this.mapBoundary) {
                    let result = await new Promise(resolve => this.mapBoundary.get(name, resolve));
                    if (result && result.boundaries && result.boundaries.length > 0) {
                        return result.boundaries;
                    }
                } else {
                    await new Promise(resolve => setTimeout(resolve, 100));
                    return this.getBoundary(name);
                }
            }
        },
        setViewRect(rect) {
            let coordinates = [fromLonLat([rect[0], rect[1]]), fromLonLat([rect[2], rect[3]])];
            this.setViewport(coordinates, 15);
        },
        distanceTextSizeHandler() {
            if (!this.distanceText || this.compact) {
                return;
            }
            let textRef = this.$refs.distanceText;
            let startRouteRef = this.$refs.startRoute;
            let throughPositionsRef = this.$refs.throughPositions;
            if (textRef && startRouteRef && throughPositionsRef) {
                let distanceTextSize = [textRef.offsetWidth, textRef.offsetHeight];
                if (!this.minDistanceTextSize) {
                    this.minDistanceTextSize = distanceTextSize;
                }
                let left = startRouteRef.offsetLeft;
                let width = throughPositionsRef.offsetLeft + throughPositionsRef.offsetWidth - left;
                textRef.style.transform = "none";
                if (width < this.minDistanceTextSize[0]) {
                    let delta = this.minDistanceTextSize[0] - width;
                    left -= Math.round(delta / 2);
                    width += delta;
                    left = Math.max(10, left);
                    width = Math.min(width, this.getClientSize()[0] - 20);
                }
                textRef.style.left = left + "px";
                textRef.style.width = width + "px";
            } else {
                setTimeout(() => this.distanceTextSizeHandler(), 100);
            }
        },
        sizeHandler() {
            this.mapHeight = this.getClientSize()[1] + "px";
            this.distanceTextSizeHandler();
        },
    },
    watch: {
        searchInput: _.debounce(function (val) {
            this.onKeywordChanged();
            if (!this.isBaiduLocate(val) && !this.isLonLatLocate(val)) {
                urlUtils.setParams({keyword: val || ""});
            }
        }, 300),
        startText: _.debounce(function () {
            this.queryStartItems();
        }, 300),
        targetText: _.debounce(function () {
            this.queryTargetItems();
        }, 300),
        showTrail(val) {
            this.updateSelectedVesselLayer();
            this.updatePlotLayer();
            urlUtils.setParams({trail: val ? "1" : ""});
        },
        washTrail(val) {
            if (this.selectedItem && this.selectedItem.class === "vessel") {
                this.selectSearchResult({class: "vessel", mmsi: this.selectedItem.mmsi}, false, false, true);
            }
            urlUtils.setParams({trailWash: val ? "1" : "0"});
        },
        startTrailDate(val) {
            this.updateSelectedVesselLayer();
            this.updatePlotLayer();
            urlUtils.setParams({trailFrom: this.getDateText(val) || ""});
        },
        endTrailDate(val) {
            this.updateSelectedVesselLayer();
            this.updatePlotLayer();
            urlUtils.setParams({trailTo: this.getDateText(val) || ""});
        },
        showThermo(val) {
            this.updateVesselThermoLayer();
            urlUtils.setParams({thermo: val ? "1" : ""});
        },
        showBerth(val) {
            this.updateVesselBerthLayer();
            urlUtils.setParams({berth: val ? "1" : ""});
        },
    },
    async mounted() {
        if (urlUtils.getParam("compact")) {
            let params = urlUtils.getParams();
            delete params.compact;
            let starts = (params.start || "").split(",").map(x => (x || "").trim()).filter(x => x);
            let targets = (params.target || "").split(",").map(x => (x || "").trim()).filter(x => x);
            let waypoints = (params.waypoints || "").split(",").map(x => (x || "").trim()).filter(x => x);
            params.start = starts[0] || "";
            params.target = targets[0] || "";
            params.waypoints = starts.slice(1).concat(waypoints).concat(targets.slice(1));
            this.fullscreenUrl = "/vue/search-map#/?" + Object.getOwnPropertyNames(params).map(f => `${f}=${encodeURIComponent(params[f])}`).join("&");
        }

        $(document.body).css("overflow", "hidden");
        this.selectedOccupiedRects = [];
        this.contextMenuHandler = () => this.newPlot = this.newLinePlot = this.newRectPlot = false;
        this.$refs.map.addEventListener("contextmenu", this.contextMenuHandler);
        this.districtBoundaryLayer = new VectorLayer({
            source: new VectorSource({features: []}),
            style: new Style({
                fill: new Fill({color: 'rgba(255, 255, 255, 0.2)'}),
                stroke: new Stroke({color: 'navy', width: 2}),
            }),
        });
        this.linePlotLayer = new VectorLayer({source: new VectorSource({features: []})});
        this.rectPlotLayer = new VectorLayer({source: new VectorSource({features: []})});
        this.plotLayer = new VectorLayer({source: new VectorSource({features: []})});
        this.plotCursorLayer = new VectorLayer({source: new VectorSource({features: []})});
        this.vesselLayer = new VectorLayer({source: new VectorSource({features: []})});
        this.groupLayer = new VectorLayer({source: new VectorSource({features: []})});
        this.selectedPlotLayer = new VectorLayer({source: new VectorSource({features: []})});
        this.selectedVesselLayer = new VectorLayer({source: new VectorSource({features: []})});
        this.vesselThermoLayer = new HeatmapLayer({
            source: new VectorSource({features: []}),
            blur: 8,
            radius: 5,
            opacity: 0.8,
        });
        this.vesselBerthLayer = new VectorLayer({source: new VectorSource({features: []})});
        this.vesselRouteLayer = new VectorLayer({source: new VectorSource({features: []})});
        this.vesselRouteRectLayer = new VectorLayer({source: new VectorSource({features: []})});
        this.measureSource = new VectorSource();
        this.measureLayer = new VectorLayer({
            source: this.measureSource,
            style: new Style({
                fill: new Fill({color: 'rgba(255, 255, 255, 0.2)'}),
                stroke: new Stroke({color: 'orangered', width: 2}),
                image: new Circle({radius: 7, fill: new Fill({color: 'orangered'})}),
            }),
        });
        this.translateFeatures = new Collection();
        this.translate = new Translate({features: this.translateFeatures});
        this.translate.on("translatestart", e => this.onTranslateStart(e));
        this.translate.on("translateend", e => this.onTranslateEnd(e));
        let center = fromLonLat([119.511392, 32.21177]);
        let zoom = 8;
        let pos = urlUtils.getParam("pos") || "";
        if (pos) {
            let comps = pos.split(",");
            center = [+comps[0] || 0, +comps[1] || 0];
            zoom = +comps[2] || 0;
        }
        this.map = new OpenLayersMap({
            target: 'ol-map-container',
            interactions: defaultInteractions({
                altShiftDragRotate: false,
                pinchRotate: false,
                doubleClickZoom: false,
            }).extend([this.translate]),
            controls: defaultControls().extend([
                new ScaleLineControl({units: 'metric'}),
                new MapTypeControl({mapType: urlUtils.getParam("mapType")}),
                new RoadTypeControl({roadType: urlUtils.getParam("roadType")}),
            ]),
            loadTilesWhileInteracting: true,
            view: new View({center, zoom, maxZoom: 19}),
            layers: getLayers().concat([
                this.vesselThermoLayer,
                this.districtBoundaryLayer,
                this.rectPlotLayer,
                this.vesselRouteRectLayer,
                this.linePlotLayer,
                this.vesselLayer,
                this.plotLayer,
                this.plotCursorLayer,
                this.groupLayer,
                this.vesselRouteLayer,
                this.vesselBerthLayer,
                this.selectedPlotLayer,
                this.selectedVesselLayer,
                this.measureLayer,
            ]),
        });
        this.map.on("moveend", () => {
            this.loadPlots();
            this.loadVessels();
            let view = this.map.getView();
            let zoom = view.getZoom();
            let center = view.getCenter();
            let pos = [center[0], center[1], zoom].map(x => +(x.toFixed(3))).join();
            let projection = view.getProjection();
            let resolution = view.getResolutionForZoom(zoom);
            this.getRes = coordinate => getPointResolution(projection, resolution, coordinate, Units.METERS);
            urlUtils.setParams({pos: pos});
            this.updatePlotLayer();
            this.updateSelectedVesselLayer();
        });
        this.map.on("pointermove", e => this.onPointerMove(e));
        this.map.on('singleclick', e => this.onClick(e));
        // this.map.on('dblclick', e => this.onDoubleClick(e));
        this.map.on("mapTypeChanged", e => urlUtils.setParams({mapType: e.mapType}));
        this.map.on("roadTypeChanged", e => urlUtils.setParams({roadType: e.roadType}));
        this.map.getViewport().addEventListener('mouseout',
            () => this.helpTooltipElement && this.helpTooltipElement.classList.add('hidden')
        );
        await this.initPlotTypes();
        this.initVessels();
        let plotTypes = urlUtils.getParam("plot_types") || "";
        let typesSet = new Set(plotTypes.split(",").filter(x => x));
        this.markerTypes.forEach(x => x.value = typesSet.has(x.name));
        this.loadPlots();
        this.updateLinePlotLayer();
        this.updateRectPlotLayer();
        this.delegation = +urlUtils.getParam("delegation") || null;
        this.searchInput = urlUtils.getParam("keyword") || "";
        this.showTrail = !!urlUtils.getParam("trail");
        let trailFrom = urlUtils.getParam("trailFrom");
        let trailTo = urlUtils.getParam("trailTo");
        this.startTrailDate = trailFrom ? new Date(moment(trailFrom).valueOf()) : this.startTrailDate;
        this.endTrailDate = trailTo ? new Date(moment(trailTo).valueOf()) : this.endTrailDate;
        this.washTrail = urlUtils.getParam("trailWash") != '0';
        this.compact = !!urlUtils.getParam("compact");
        this.showThermo = !!urlUtils.getParam("thermo");
        this.showBerth = !!urlUtils.getParam("berth");
        this.startText = urlUtils.getParam("start") || "";
        this.targetText = urlUtils.getParam("target") || "";
        this.maxWeight = parseInt(urlUtils.getParam("max_weight")) || null;
        this.waypoints = (urlUtils.getParam("waypoints") || "").split(",").filter(x => x);
        if (this.startText && this.targetText) {
            this.queryRoute();
        }
        let viewRect = urlUtils.getParam("viewRect");
        let selectItem = urlUtils.getParam("select");
        if (selectItem) {
            let index = selectItem.indexOf("_");
            if (index >= 0) {
                let item = {class: selectItem.slice(0, index)};
                switch (item.class) {
                    case "plot":
                        item.id = +selectItem.slice(index + 1) || 0;
                        break;
                    case "vessel":
                        item.mmsi = +selectItem.slice(index + 1) || 0;
                        if (this.delegation && this.mmsi) {
                            this.delegationMmsi = item.mmsi;
                        }
                        break;
                    case "district":
                        item.displayName = selectItem.slice(index + 1);
                        break;
                    default:
                        item = null;
                        break;
                }
                await this.selectSearchResult(item, !!pos || !!viewRect, true);
            }
        }
        if (viewRect) {
            urlUtils.setParams({viewRect: null});
            let rect = viewRect.split(",").map(x => +x).filter(x => x);
            if (rect.length >= 4) {
                this.setViewRect(rect);
            }
        }
    },
    async created() {
        this.groups = [];
        this.plots = [];
        this.vesselRoute = [];
        this.vesselRouteRects = [];
        this.editIdSet = new Set();
        this.initBaiduMap();
        let params = {};
        let mmsi = +this.$route.query["mmsi"] || 0;
        if (mmsi) {
            params.select = `vessel_${mmsi}`;
            let vesselName = this.$route.query["vesselName"];
            if (vesselName) {
                params.keyword = this.getStringName(vesselName);
            }
        }
        let startText = this.$route.query["startText"];
        if (startText) {
            params.start = startText;
        }
        let targetText = this.$route.query["targetText"];
        if (targetText) {
            params.target = targetText;
        }
        let maxWeight = +this.$route.query["maxWeight"] || null;
        if (maxWeight) {
            params.max_weight = maxWeight;
        }
        let waypoints = this.$route.query["throughPositions"] || "";
        if (waypoints) {
            params.waypoints = waypoints;
        }
        let delegation = +this.$route.query["delegation"] || null;
        if (delegation) {
            params.delegation = delegation;
        }
        let mapType = this.$route.query["mapType"] || null;
        if (mapType) {
            params.mapType = mapType;
        }
        let roadType = this.$route.query["roadType"] || null;
        if (roadType) {
            params.roadType = roadType;
        }
        let compact = this.$route.query["compact"] || null;
        if (compact) {
            params.compact = compact;
        }
        let trail = this.$route.query["trail"] || null;
        if (trail) {
            params.trail = trail;
        }
        let trailFrom = this.$route.query["trailFrom"] || null;
        if (trailFrom) {
            params.trailFrom = trailFrom;
        }
        let trailTo = this.$route.query["trailTo"] || null;
        if (trailTo) {
            params.trailTo = trailTo;
        }
        let trailWash = this.$route.query["trailWash"] || null;
        if (trailWash) {
            params.trailWash = trailWash;
        }
        let berth = this.$route.query["berth"] || null;
        if (berth) {
            params.berth = berth;
        }
        let plot_types = this.$route.query["plot_types"] || null;
        if (plot_types) {
            params.plot_types = plot_types;
        }
        let viewRect = this.$route.query["viewRect"] || null;
        if (viewRect) {
            params.viewRect = viewRect;
        }
        urlUtils.queryToParams(params);

        this.clickOutHandler = e => {
            let target = e.target || e.srcElement;
            const hasParent = function hasParent(node, parent) {
                while (node) {
                    if (node === parent) {
                        return true;
                    }
                    node = node.parentNode;
                }
                return false;
            };
            if (!hasParent(target, this.$refs.search)) {
                this.showSearchResult = false;
                this.$refs.searchInput.blur();
            }
            if (!hasParent(target, this.$refs.startRoute)) {
                this.showStartItems = false;
                this.$refs.routeStartInput.blur();
            }
            if (!hasParent(target, this.$refs.targetRoute)) {
                this.showTargetItems = false;
                this.$refs.routeTargetInput.blur();
            }
        };
        document.body.addEventListener("click", this.clickOutHandler);
        window.onresize = this.sizeHandler;
        this.sizeHandler();
    },
    deactivated() {
        this.$refs.map && this.$refs.map.removeEventListener("contextmenu", this.contextMenuHandler);
    },
    destroyed() {
        $(document.body).css("overflow", "");
        document.body.removeEventListener("click", this.clickOutHandler);
    },
}
</script>

<style scoped>
.map-wrapper {
    position: relative;
}

.map-menu-bar {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    padding: 10px;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: stretch;
}

.distance-text {
    display: block;
    position: absolute;
    top: 50px;
    left: 50%;
    width: auto;
    transform: translateX(-50%);
    padding: 0.125rem 0.5rem;
    font-size: 1rem;
    font-weight: normal;
    color: #333;
    background-color: #f9f9f9;
    background-clip: padding-box;
    border: 1px solid #ced4da;
    border-radius: 0.25rem;
}

.compact-map .distance-text {
    display: none;
}

.search-input {
    width: 30%;
    max-width: 500px;
    position: relative;
}

.route-panel {
    flex: 1;
    margin-left: 1rem;
    background-color: transparent;
    height: 38px;
    line-height: 38px;
    display: flex;
}

.config-buttons {
    margin-left: 1rem;
    display: flex;
}

.config-buttons > * + * {
    margin-left: 0.5rem;
}

.full-screen-button {
    display: none;
}

.compact-map .search-input,
.compact-map .route-panel {
    display: none;
}

.compact-map .config-buttons {
    flex: 1;
    margin: 0;
}

.compact-map .full-screen-button {
    display: block;
}

.vessel-display-status {
    position: absolute;
    left: 0;
    bottom: 10px;
    width: 100%;
    text-align: center;
    pointer-events: none;
}

.vessel-display-status > span {
    padding: 5px 10px;
    color: #856404;
    background-color: #fff3cd;
    border-color: #ffeeba;
    border-radius: 5px;
    line-height: 40px;
    font-size: 0.8rem;
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.3);
}

.tools-vessel-filter {
    width: 170px;
}

.marker-dropdown {
    width: 170px;
}

.tools-dropdown {
    width: 170px;
}

.route-dropdown {
}

.route-input {
}

.search-input input {
    padding-left: 38px;
    padding-right: 30px;
}

.no-pointer-events {
    pointer-events: none;
}

.search-input .search-icon {
    position: absolute;
    left: 9px;
    top: 9px;
    pointer-events: none;
}

.search-input .clear-icon {
    position: absolute;
    right: 9px;
    top: 9px;
}

.map-tooltip {
    position: absolute;
    top: 0;
    left: 0;
    padding: .25em .5em;
    background: white;
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: 5px;
    z-index: 100;
}

.vessel-info-panel {
    position: absolute;
    top: 150px;
    right: 10px;
    background-color: white;
    border: 1px solid #ced4da;
    border-radius: 0.25rem;
    padding: 1rem;
    min-width: 200px;
}

.compact-map .vessel-info-panel {
    top: 60px;
}

.vessel-info-panel > * + * {
    margin-top: .25rem;
}

@media (max-width: 1440px) {
    .route-panel {
        width: calc(50% - 1rem);
    }

    .config-buttons {
        width: 100%;
        align-items: flex-end;
        margin-left: 0;
        margin-top: .5rem;
    }
}

@media (max-width: 900px) {
    .vessel-display-status {
        bottom: 60px;
    }
}

@media (max-width: 768px) {
    .search-input {
        width: 100%;
        max-width: 100%;
    }

    .route-panel {
        width: 100%;
        margin-left: 0;
        margin-top: .5rem;
    }

    .config-buttons {
        width: 100%;
        align-items: flex-end;
        margin-left: 0;
        margin-top: .5rem;
    }

    .vessel-info-panel {
        background-color: rgba(255, 255, 255, 0.5);
    }

    .distance-text {
        top: 98px;
    }
}

.info-date-picker {
    margin-left: 24px;
    width: 130px;
}

@media (max-width: 512px) {
    .vessel-display-status {
        bottom: 100px;
    }
}

.panel-info-line {
    display: flex;
    align-items: center;
}

.panel-info-label {
    width: 80px;
    color: #6c757d;
    text-align: right;
    margin-right: 0.5rem;
}

.panel-info-label.long-text {
    width: 90px;
    margin-left: -10px;
    font-size: 15px;
}

.panel-info-content {
    flex: 1;
    max-width: 190px;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
}

.btn-outline-primary.semi-transparent-btn {
    background-color: rgba(255, 255, 255, 0.8);
}

.btn-outline-primary.semi-transparent-btn:hover {
    background-color: #007bff;
}
</style>

<style>
.ol-map-type {
    bottom: 10px;
    left: 10px;
}

.ol-road-type {
    bottom: 10px;
    left: 160px;
}

.ol-control.ol-map-type, .ol-control.ol-road-type {
    display: flex;
}

.ol-control.ol-map-type button, .ol-control.ol-road-type button {
    width: auto;
    height: auto;
    line-height: normal;
    font-size: 18px;
    font-weight: normal;
    padding: 5px 10px;
}

.ol-control.ol-map-type button.selected, .ol-control.ol-road-type button.selected {
    text-decoration: none;
    background-color: rgba(0, 60, 136, .8);
}

.ol-scale-line {
    top: auto;
    left: auto;
    right: 10px;
    bottom: 10px;
}

.compact-map .ol-scale-line {
    left: 60px;
    right: auto;
    bottom: 60px;
}

.ol-zoom {
    left: auto;
    top: auto;
    right: 10px;
    bottom: 40px;
}

.compact-map .ol-zoom {
    left: 10px;
    right: auto;
    bottom: 60px;
}

.ol-zoom button {
    font-size: 1.5rem;
}

@media (max-width: 512px) {
    .ol-road-type {
        bottom: 55px;
        left: 10px;
    }
}

.ol-tooltip {
    position: relative;
    background: rgba(0, 0, 0, 0.5);
    border-radius: 4px;
    color: white;
    padding: 4px 8px;
    opacity: 0.7;
    white-space: nowrap;
    font-size: 12px;
}

.ol-tooltip-measure {
    opacity: 1;
    font-weight: bold;
}

.ol-tooltip-static {
    background-color: orangered;
    color: white;
    border: 1px solid white;
}

.ol-tooltip-measure:before,
.ol-tooltip-static:before {
    border-top: 6px solid rgba(0, 0, 0, 0.5);
    border-right: 6px solid transparent;
    border-left: 6px solid transparent;
    content: "";
    position: absolute;
    bottom: -6px;
    margin-left: -7px;
    left: 50%;
}

.ol-tooltip-static:before {
    border-top-color: orangered;
}

.ol-tooltip-static-close-btn {
    font-size: 1.4rem;
    line-height: 0.8rem;
    height: 1rem;
    cursor: pointer;
}

.ol-tooltip-static-close-btn:hover {
}

.ol-tooltip-static-close-btn:active {
}

.ol-event-transparent {
    pointer-events: none;
}

.ol-event-solid {
    pointer-events: auto;
}

.ol-circle {
    background: transparent;
    width: 10px;
    height: 10px;
    border: 1px solid black;
    border-radius: 5px;
}
</style>
